import { DateTime } from 'luxon'

import SATSeriesQ from '@/queries/SATSeriesQ'
import SeriesQ from '@/queries/SeriesQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import SessionQ from '@/queries/SessionQ'
import UserQ from '@/queries/UserQ'
import type { Query } from '@/client/query'
import { SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES } from '@/client/services/programs/ka-learning-labs/algebra2/constants'
import { getTutorOfferingPageUrl } from '@/client/services/url'
import { isProgramWorld } from '@/client/services/worlds/helpers'

import { SAT_BOOTCAMP_WORLD_SLUG, SAT_PREP_WORLD_SLUG } from '../worlds/constants'
import { PROGRAM_SLUG_TO_PROGRAM_DETAILS } from './config'
import {
  POPULAR_RECURRING_PROGRAM_TIME_RANGES,
  PROGRAM_SLUGS,
  PROGRAM_SLUGS_WITH_WORLD,
  PROGRAM_WORLD_TO_PROGRAM_SLUG,
  type ProgramSlug,
  type ProgramSlugWithWorld,
  SAT_BOOTCAMP_PROGRAM_SLUG,
} from './constants'
import type { ProgramSeriesDetails, ProgramType } from './types'

export function getCapitalizedAndOrPluralizedProgramType({
  programType,
  pluralize = false,
  capitalize = false,
}: {
  programType: ProgramType
  pluralize?: boolean
  capitalize?: boolean
}): string {
  let result: string = programType

  if (pluralize) {
    result += result.endsWith('s') ? '' : 's'
  }

  if (capitalize) {
    result = result
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return result
}

export function programSeriesDetailsFromQuery(series: Query): ProgramSeriesDetails {
  const sessionStartDates = series
    .many(SessionQ.where({ seriesId: SeriesQ.id, order: [[SessionQ.start_date, 'asc']] }))
    .map(sessionItem => `${sessionItem(SessionQ.start_date)}`)
  const seriesTemplate = series.one(SeriesQ.getSeriesTemplate())
  const satSeries = series.one(SATSeriesQ.where({ seriesId: SeriesQ.id }))
  const tutor = series.one(UserQ.where({ id: SeriesQ.user_id }))

  return {
    id: series(SeriesQ.id) ?? 0,
    templateSlug: seriesTemplate(SeriesTemplateQ.slug) ?? '',
    sessionStartDates,
    tutorName: tutor(UserQ.display_name) ?? '',
    tutorAvatarUrl: tutor(UserQ.avatar_url) ?? '',
    tutorBio: tutor(UserQ.bio) ?? '',
    countRegistrationsAndReservations:
      series(SeriesQ.countRegistrationsAndActiveReservations()) ?? 0,
    maxLearners: series(SeriesQ.max_learners) ?? 0,
    attendancePolicy: series(SeriesQ.attendance_policy) ?? '',
    questionnaire: series(SeriesQ.questionnaire) ?? null,
    researchGroup: series(SeriesQ.getResearchGroup()) ?? null,
    satTestId: satSeries(SATSeriesQ.sat_test_id),
  }
}

export function getProgramPopularTimeRange(type: 'weekday' | 'weekend'): string {
  const startDateTime = DateTime.fromObject(
    {
      hour: POPULAR_RECURRING_PROGRAM_TIME_RANGES[type].startHour,
    },
    {
      zone: POPULAR_RECURRING_PROGRAM_TIME_RANGES.timezone,
    }
  ).setZone('local')
  const endDateTime = DateTime.fromObject(
    {
      hour: POPULAR_RECURRING_PROGRAM_TIME_RANGES[type].endHour,
    },
    {
      zone: POPULAR_RECURRING_PROGRAM_TIME_RANGES.timezone,
    }
  ).setZone('local')

  const startDateTimeString = startDateTime.toLocaleString(DateTime.TIME_SIMPLE)
  const endDateTimeString = endDateTime.toLocaleString(DateTime.TIME_SIMPLE)

  return `${startDateTimeString} - ${endDateTimeString} ${startDateTime.offsetNameShort}`
}

export function getProgramComplementForCommunitySubworld(
  worldSlug: string
): { programSlug: ProgramSlugWithWorld; title: string; worldSlug: string } | null {
  if (worldSlug === SAT_PREP_WORLD_SLUG) {
    return {
      programSlug: SAT_BOOTCAMP_PROGRAM_SLUG,
      title: 'SAT Bootcamp',
      worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
    }
  }

  for (const programSlug of PROGRAM_SLUGS_WITH_WORLD) {
    if (programSlug !== SAT_BOOTCAMP_PROGRAM_SLUG) {
      const programDetails = PROGRAM_SLUG_TO_PROGRAM_DETAILS[programSlug]
      if (programDetails.fallbackWorldSlug === worldSlug) {
        return { programSlug, title: programDetails.title, worldSlug: programDetails.worldSlug }
      }
    }
  }
  return null
}

export function isDuringAlgebra2FinalExamPrepExperiment(): boolean {
  return (
    DateTime.now() < SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-6'].plus({ weeks: 4 })
  )
}

export function isProgramSlug(slug: string): slug is ProgramSlug {
  return PROGRAM_SLUGS.some(programSlug => programSlug === slug)
}

const PROGRAMS_REQUIRING_CONSENT = new Set([SAT_BOOTCAMP_PROGRAM_SLUG])
const PROGRAMS_REQUIRING_ASSENT = new Set([SAT_BOOTCAMP_PROGRAM_SLUG])

export function needsConsentForProgram(programSlug: string): {
  needsParentConsent: boolean
  needsAssent: boolean
} {
  return {
    needsParentConsent: PROGRAMS_REQUIRING_CONSENT.has(programSlug) ?? false,
    needsAssent: PROGRAMS_REQUIRING_ASSENT.has(programSlug) ?? false,
  }
}

export function getTutorOfferingPageUrlByWorld(worldSlug: string, subpage?: 'certify'): string {
  if (isProgramWorld(worldSlug)) {
    return getTutorOfferingPageUrl({
      type: 'program',
      slug: PROGRAM_WORLD_TO_PROGRAM_SLUG[worldSlug],
      subpage,
    })
  }
  return getTutorOfferingPageUrl({ type: 'subject', slug: worldSlug, subpage })
}
